/**
 * Language Context for the Student Immunization Portal
 * Provides internationalization support with 10 languages
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import i18n from 'i18next';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  detectLanguage as detectPreferredLanguage,
  isSupportedLanguage,
  storeLanguage,
} from '../utils/languagePreference';

export type { SupportedLanguage } from '../utils/languagePreference';

/**
 * Language information
 */
export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
  rtl: boolean;
}

/**
 * Available languages
 */
export const AVAILABLE_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen', flag: '🇭🇹', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', rtl: false },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', rtl: false },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', rtl: false },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', rtl: false },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', rtl: false },
];

/**
 * Language state interface
 */
export interface LanguageState {
  currentLanguage: SupportedLanguage;
  isRTL: boolean;
  isLoading: boolean;
  detectedLanguage: SupportedLanguage | null;
}

/**
 * Language context interface
 */
export interface LanguageContextValue extends LanguageState {
  // Language management
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  detectLanguage: () => SupportedLanguage;
  
  // Language info
  availableLanguages: LanguageInfo[];
  getCurrentLanguageInfo: () => LanguageInfo;
  getLanguageInfo: (code: SupportedLanguage) => LanguageInfo | undefined;
  
  // Translation helpers
  t: (key: string, options?: Record<string, any>) => string;
  formatDate: (date: Date | string, format?: string) => string;
  formatNumber: (number: number, options?: Intl.NumberFormatOptions) => string;
}

/**
 * Default context value
 */
const defaultContextValue: LanguageContextValue = {
  currentLanguage: DEFAULT_LANGUAGE,
  isRTL: false,
  isLoading: true,
  detectedLanguage: null,
  setLanguage: async () => {},
  detectLanguage: () => DEFAULT_LANGUAGE,
  availableLanguages: AVAILABLE_LANGUAGES,
  getCurrentLanguageInfo: () => AVAILABLE_LANGUAGES[0],
  getLanguageInfo: () => undefined,
  t: (key) => key,
  formatDate: (date) => String(date),
  formatNumber: (num) => String(num),
};

/**
 * Language context
 */
const LanguageContext = createContext<LanguageContextValue>(defaultContextValue);

/**
 * Language provider props
 */
interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: SupportedLanguage;
}

/**
 * Language provider component
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage = DEFAULT_LANGUAGE 
}) => {
  const [state, setState] = useState<LanguageState>({
    currentLanguage: defaultLanguage,
    isRTL: false,
    isLoading: true,
    detectedLanguage: null,
  });

  /**
   * Detect the user's language: `?lang=` → stored preference → browser → English.
   * Same resolution as the i18next bootstrap (utils/languagePreference).
   */
  const detectLanguage = useCallback((): SupportedLanguage => detectPreferredLanguage(), []);

  /**
   * Set language (i18next, document direction/lang, persisted preference).
   */
  const setLanguage = useCallback(async (requested: SupportedLanguage): Promise<void> => {
    let language = requested;
    if (!isValidLanguage(language)) {
      console.warn(`Invalid language: ${language}, falling back to ${DEFAULT_LANGUAGE}`);
      language = DEFAULT_LANGUAGE;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Change i18next language
      await i18n.changeLanguage(language);

      // Get RTL status
      const languageInfo = AVAILABLE_LANGUAGES.find(l => l.code === language);
      const isRTL = languageInfo?.rtl || false;

      // Update document direction
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = language;

      // Store preference (single key read by every consumer, incl. API clients)
      storeLanguage(language);

      setState(prev => ({
        ...prev,
        currentLanguage: language,
        isRTL,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to change language:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  /**
   * Get current language info
   */
  const getCurrentLanguageInfo = useCallback((): LanguageInfo => {
    return AVAILABLE_LANGUAGES.find(l => l.code === state.currentLanguage) || AVAILABLE_LANGUAGES[0];
  }, [state.currentLanguage]);

  /**
   * Get language info by code
   */
  const getLanguageInfo = useCallback((code: SupportedLanguage): LanguageInfo | undefined => {
    return AVAILABLE_LANGUAGES.find(l => l.code === code);
  }, []);

  /**
   * Translation function
   */
  const t = useCallback((key: string, options?: Record<string, any>): string => {
    return i18n.t(key, options) as string;
  }, []);

  /**
   * Format date for current locale
   */
  const formatDate = useCallback((date: Date | string | undefined | null, format?: string): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return String(date);
    }

    const localeMap: Record<SupportedLanguage, string> = {
      'en': 'en-US',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ht': 'fr-HT',
      'pt': 'pt-BR',
      'zh': 'zh-CN',
      'vi': 'vi-VN',
      'ar': 'ar-SA',
      'ko': 'ko-KR',
      'ru': 'ru-RU',
    };

    const locale = localeMap[state.currentLanguage] || 'en-US';

    try {
      if (format === 'short') {
        return dateObj.toLocaleDateString(locale);
      } else if (format === 'long') {
        return dateObj.toLocaleDateString(locale, { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } else if (format === 'time') {
        return dateObj.toLocaleTimeString(locale);
      } else if (format === 'datetime') {
        return dateObj.toLocaleString(locale);
      }
      
      return dateObj.toLocaleDateString(locale);
    } catch {
      return dateObj.toLocaleDateString();
    }
  }, [state.currentLanguage]);

  /**
   * Format number for current locale
   */
  const formatNumber = useCallback((
    number: number, 
    options?: Intl.NumberFormatOptions
  ): string => {
    const localeMap: Record<SupportedLanguage, string> = {
      'en': 'en-US',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ht': 'fr-HT',
      'pt': 'pt-BR',
      'zh': 'zh-CN',
      'vi': 'vi-VN',
      'ar': 'ar-SA',
      'ko': 'ko-KR',
      'ru': 'ru-RU',
    };

    const locale = localeMap[state.currentLanguage] || 'en-US';

    try {
      return new Intl.NumberFormat(locale, options).format(number);
    } catch {
      return String(number);
    }
  }, [state.currentLanguage]);

  // Initialize language on mount
  useEffect(() => {
    const initLanguage = async () => {
      const detected = detectLanguage();
      
      setState(prev => ({ ...prev, detectedLanguage: detected }));
      
      await setLanguage(detected);
    };

    initLanguage();
  }, [detectLanguage, setLanguage]);

  /**
   * Context value
   */
  const value = useMemo<LanguageContextValue>(() => ({
    ...state,
    setLanguage,
    detectLanguage,
    availableLanguages: AVAILABLE_LANGUAGES,
    getCurrentLanguageInfo,
    getLanguageInfo,
    t,
    formatDate,
    formatNumber,
  }), [
    state,
    setLanguage,
    detectLanguage,
    getCurrentLanguageInfo,
    getLanguageInfo,
    t,
    formatDate,
    formatNumber,
  ]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 */
export const useLanguageContext = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  
  return context;
};

/**
 * Check if language code is valid
 */
const isValidLanguage = (code: string): boolean => isSupportedLanguage(code);

/**
 * Get language direction class
 */
export const getDirectionClass = (isRTL: boolean): string => {
  return isRTL ? 'rtl' : 'ltr';
};

/**
 * RTL-aware margin/padding classes
 */
export const rtlClasses = {
  marginLeft: (isRTL: boolean) => isRTL ? 'mr-' : 'ml-',
  marginRight: (isRTL: boolean) => isRTL ? 'ml-' : 'mr-',
  paddingLeft: (isRTL: boolean) => isRTL ? 'pr-' : 'pl-',
  paddingRight: (isRTL: boolean) => isRTL ? 'pl-' : 'pr-',
  textAlign: (isRTL: boolean) => isRTL ? 'text-right' : 'text-left',
  flexDirection: (isRTL: boolean) => isRTL ? 'flex-row-reverse' : 'flex-row',
};

export default LanguageContext;